import React, { useState, useEffect } from "react";
import { useAccountDetailsContext } from "../AccountDetailsContainer";
import PaymentMethodSection from "./PaymentMethodSection";
import { formatPhone, formatEIN, maskEIN, einLabelForCountry, einPlaceholderForCountry, einMaxLengthForCountry } from "../../../shared/formattingUtils";
import { formatPostalCode } from "../../../shared/postalCodeUtils";
import { toastSuccess } from "../../../shared/toastHelper";
import {
  COUNTRY_CONFIG,
  COUNTRY_OPTIONS,
  getRegionsForCountry,
  getRegionLabelForCountry,
} from "../../CustomFieldValues/constants/customFieldValuesConstants";

const AccountDetails = ({ onSave, showPaymentMethods = true }) => {
  const { team, saving, updateTeam, businessTypes } = useAccountDetailsContext();

  const [formState, setFormState] = useState({
    legal_business_name: team.legal_business_name || "",
    business_type: team.business_type || "",
    ein: team.ein ? formatEIN(team.ein, team.business_country || "US") : "",
    website_url: team.website_url || "",
    business_street_address: team.business_street_address || "",
    business_city: team.business_city || "",
    business_region: team.business_region || "",
    business_postal_code: team.business_postal_code ? formatPostalCode(team.business_postal_code) : "",
    business_country: team.business_country || "US",
    billing_contact_first_name: team.billing_contact_first_name || "",
    billing_contact_last_name: team.billing_contact_last_name || "",
    billing_contact_email: team.billing_contact_email || "",
    billing_contact_phone: team.billing_contact_phone ? formatPhone(team.billing_contact_phone) : "",
    billing_contact_title: team.billing_contact_title || "",
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [einFocused, setEinFocused] = useState(false);
  const [einEdited, setEinEdited] = useState(false);

  useEffect(() => {
    const changed = Object.keys(formState).some((key) => {
      const original = team[key] || "";
      return formState[key] !== original;
    });
    setHasChanges(changed);
  }, [formState, team]);

  const handleChange = (field) => (e) => {
    setFormState((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleEINChange = (e) => {
    setEinEdited(true);
    setFormState((prev) => ({
      ...prev,
      ein: formatEIN(e.target.value, prev.business_country),
    }));
  };

  const getEINDisplayValue = () => {
    if (einFocused || einEdited) {
      return formState.ein;
    }
    if (team.ein) {
      return maskEIN(team.ein, formState.business_country);
    }
    return formState.ein;
  };

  const handlePhoneChange = (e) => {
    setFormState((prev) => ({
      ...prev,
      billing_contact_phone: formatPhone(e.target.value),
    }));
  };

  const handlePostalCodeChange = (e) => {
    setFormState((prev) => ({
      ...prev,
      business_postal_code: formatPostalCode(e.target.value, prev.business_country),
    }));
  };

  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setEinEdited(true);
    setFormState((prev) => ({
      ...prev,
      business_country: newCountry,
      business_region: "",
      business_postal_code: "",
      ein: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedTeam = await updateTeam(formState);
    if (updatedTeam) {
      if (onSave) {
        onSave(updatedTeam);
      } else {
        toastSuccess("Account details saved successfully");
      }
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="font-weight-bold mb-0">Business Information</h5>
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={saving || !hasChanges}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Legal Business Name</label>
              <input
                type="text"
                className="form-control"
                value={formState.legal_business_name}
                onChange={handleChange("legal_business_name")}
                placeholder="e.g., Acme Entertainment LLC"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Business Type</label>
              <select
                className="form-control"
                value={formState.business_type}
                onChange={handleChange("business_type")}
              >
                <option value="">Select Business Type</option>
                {businessTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">{einLabelForCountry(formState.business_country)}</label>
              <input
                type="text"
                className="form-control"
                value={getEINDisplayValue()}
                onChange={handleEINChange}
                onFocus={() => setEinFocused(true)}
                onBlur={() => setEinFocused(false)}
                placeholder={einPlaceholderForCountry(formState.business_country)}
                maxLength={einMaxLengthForCountry(formState.business_country)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Website URL</label>
              <input
                type="url"
                className="form-control"
                value={formState.website_url}
                onChange={handleChange("website_url")}
                placeholder="https://example.com"
              />
            </div>
          </div>
        </div>

        <hr className="my-4" />

        <div className="mb-4">
          <h5 className="font-weight-bold mb-3">Business Address</h5>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Country</label>
              <select
                className="form-control"
                value={formState.business_country}
                onChange={handleCountryChange}
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label text-muted small font-weight-medium">Street Address</label>
              <input
                type="text"
                className="form-control"
                value={formState.business_street_address}
                onChange={handleChange("business_street_address")}
                placeholder="123 Main Street"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">City</label>
              <input
                type="text"
                className="form-control"
                value={formState.business_city}
                onChange={handleChange("business_city")}
                placeholder="City"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">
                {getRegionLabelForCountry(formState.business_country)}
              </label>
              <select
                className="form-control"
                value={formState.business_region}
                onChange={handleChange("business_region")}
              >
                <option value="">Select {getRegionLabelForCountry(formState.business_country)}</option>
                {getRegionsForCountry(formState.business_country).map((region) => (
                  <option key={region.code} value={region.code}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">Postal Code</label>
              <input
                type="text"
                className="form-control"
                value={formState.business_postal_code}
                onChange={handlePostalCodeChange}
                placeholder={formState.business_country === "CA" ? "A1A 1A1" : "12345"}
                maxLength={formState.business_country === "CA" ? 7 : 10}
              />
            </div>
          </div>
        </div>

        <hr className="my-4" />

        <div className="mb-4">
          <h5 className="font-weight-bold mb-3">Billing Contact</h5>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">First Name</label>
              <input
                type="text"
                className="form-control"
                value={formState.billing_contact_first_name}
                onChange={handleChange("billing_contact_first_name")}
                placeholder="First Name"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">Last Name</label>
              <input
                type="text"
                className="form-control"
                value={formState.billing_contact_last_name}
                onChange={handleChange("billing_contact_last_name")}
                placeholder="Last Name"
              />
            </div>
            <div className="col-md-4 mb-3">
              <label className="form-label text-muted small font-weight-medium">Title</label>
              <input
                type="text"
                className="form-control"
                value={formState.billing_contact_title}
                onChange={handleChange("billing_contact_title")}
                placeholder="e.g., Finance Director, Owner"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Email</label>
              <input
                type="email"
                className="form-control"
                value={formState.billing_contact_email}
                onChange={handleChange("billing_contact_email")}
                placeholder="billing@example.com"
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small font-weight-medium">Phone</label>
              <input
                type="tel"
                className="form-control"
                value={formState.billing_contact_phone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                maxLength={14}
              />
            </div>
          </div>
        </div>

      </form>

      {showPaymentMethods && (
        <>
          <hr className="my-4" />
          <PaymentMethodSection />
        </>
      )}
    </div>
  );
};

export default AccountDetails;
